import { Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { WorkCalendarSummary } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Popover, usePopover } from '../../../../components/Popover';
import { useToasts } from '../../../../stores/toasts';
import { useRemoveCalendar } from '../../api/calendars-api';
import styles from './CalendarPopovers.module.css';

const CONFIRM_W = 340;

/**
 * "Remove calendar" keeps its confirm, opened beside the button rather than centred (owner, 2026-11-02):
 * there is no putting a calendar back, and what used it moves to another calendar at once.
 */
export function RemoveCalendarButton({ calendar }: { readonly calendar: WorkCalendarSummary }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const remove = useRemoveCalendar();
  const showToast = useToasts((state) => state.show);

  const confirm = (): void => {
    remove.mutate(
      { id: calendar.id, version: calendar.version },
      {
        onSuccess: () => {
          close();
          showToast({ tone: 'success', message: `${calendar.name} removed` });
        },
        onError: (error) => {
          close();
          showToast({ tone: 'danger', message: `${calendar.name} wasn't removed`, detail: error.message });
        },
      },
    );
  };

  return (
    <>
      <Button ref={triggerRef} variant="danger" icon={Trash2} aria-expanded={anchor !== null} onClick={toggle}>
        Remove
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={CONFIRM_W} label={`Remove ${calendar.name}`}>
        <div className={styles.confirm}>
          <h3 className={styles.title}>Remove {calendar.name}?</h3>
          <p className={styles.lede}>Anything using it goes back to its venture’s calendar, or to the standard week. Past time limits are not worked out again.</p>
          <div className={styles.actions}>
            <Button variant="ghost" onClick={close}>
              Keep it
            </Button>
            <Button variant="dangerSolid" isPending={remove.isPending} onClick={confirm}>
              Remove calendar
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
